<template>
    <div>
        <div v-if="locations.length > 1" class="map-popup-header">
            {{ $t('vendorLocationMapPopup.locationsHere', { count: locations.length }) }}
        </div>
        <div :class="{ 'map-popup-list': locations.length > 1 }">
            <div v-for="group in companyGroups" :key="group.company.id" class="map-popup-company">
                <a :href="group.company.url" class="company-map-link">{{ group.company.name || $t('vendorLocationMapPopup.unnamedCompany') }}</a>
                <div v-for="loc in group.locations" :key="loc.id" class="map-popup-location">
                    {{ loc.name || $t('vendorLocationMapPopup.unnamedLocation') }}
                    <div v-if="siteTypesFor(loc).length" class="d-flex flex-wrap gap-1 mt-1">
                        <CompanySiteTypeBadge v-for="siteType in siteTypesFor(loc)" :key="siteType.id" :site-type="siteType" small />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import groupBy from 'lodash/groupBy';
import CompanySiteTypeBadge from '@/components/vendors/components/CompanySiteTypeBadge.vue';

export default {
    // the content of a VendorLocationMap popup -- one or more locations (overlapping dots / a cluster on one spot), grouped by company
    // mounted by hand into a maplibre Popup (setDOMContent) -- see VendorLocationMap.showLocationsPopup
    name: 'VendorLocationMapPopup',
    components: { CompanySiteTypeBadge },
    mixins: [],
    i18n: {
        messages: {
            en: {
                vendorLocationMapPopup: {
                    unnamedCompany: 'Un-named Company',
                    unnamedLocation: 'Un-named Location',
                    locationsHere: '{count} locations here',
                },
            },
            es: {
                vendorLocationMapPopup: {
                    unnamedCompany: 'Empresa sin nombre',
                    unnamedLocation: 'Ubicación sin nombre',
                    locationsHere: '{count} ubicaciones aquí',
                },
            },
            pt: {
                vendorLocationMapPopup: {
                    unnamedCompany: 'Empresa sem nome',
                    unnamedLocation: 'Localização sem nome',
                    locationsHere: '{count} localizações aqui',
                },
            },
            'zh-Hans': {
                vendorLocationMapPopup: {
                    unnamedCompany: '未命名公司',
                    unnamedLocation: '未命名地点',
                    locationsHere: '此处有 {count} 个地点',
                },
            },
            'zh-Hant': {
                vendorLocationMapPopup: {
                    unnamedCompany: '未命名公司',
                    unnamedLocation: '未命名地點',
                    locationsHere: '此處有 {count} 個地點',
                },
            },
        },
    },
    props: {
        locations: {
            type: Array,  // CompanyLocation objects (VendorLocationMap) -- CompanySite data has site_type_ids
            required: true,
        },
        siteTypesById: {
            type: Object,  // { id: serialized CompanySiteType } -- empty for tenants without use_vendor_sites
            default: () => ({}),
        },
    },
    computed: {
        companyGroups() {
            const byCompany = groupBy(this.locations, loc => loc.company.id);
            return Object.values(byCompany)
                .map(locs => ({ company: locs[0].company, locations: locs }))
                .sort((a, b) => (a.company.name || '').localeCompare(b.company.name || ''));
        },
        hasMultiple() {
            return this.companyGroups.length > 1;
        },
    },
    methods: {
        siteTypesFor(loc) {
            return loc.site_type_ids.map(id => this.siteTypesById[id]).filter(Boolean);
        },
    },
};
</script>


<style lang="scss" scoped>
.map-popup-header {
    font-weight: bold;
    padding-bottom: 4px;
    margin-bottom: 4px;
    border-bottom: 1px solid #dee2e6;
}

.map-popup-list {
    max-height: 260px;  // a city-level geocode can stack many suppliers on one spot -- scroll instead of a giant popup
    overflow-y: auto;
}

.map-popup-location {
    border-left: 3px solid #cccccc;
    padding-left: 6px;
}

.map-popup-company + .map-popup-company {
    margin-top: 8px;
}

.map-popup-location + .map-popup-location {
    margin-top: 6px;
}

.company-map-link {
    color: #007bff;
    text-decoration: none;
    font-weight: bold;
}
</style>
